/**
 * Outreach + member geographic distribution on a US map.
 *
 * Plots one circle per US state at its centroid, sized by the selected
 * metric (Members or Sends) on a log scale. Hovering or clicking a
 * state shows exact counts. Click also zooms to that state.
 *
 * Uses Mapbox GL JS (already a project dep, configured via VITE_MAPBOX_API_KEY).
 * State centroids are hard-coded so the map needs zero extra tilesets/data
 * sources to render meaningful spatial information.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useGeoDistribution, useMemberGeoDistribution, useMemberCountyDistribution } from '@/hooks/ml';
import { US_STATE_CENTROIDS } from './usStateCentroids';
import { CA_COUNTY_CENTROIDS } from './caCountyCentroids';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_API_KEY ?? '';

interface MarkerData {
  state: string;
  value: number;          // count of the active metric (members or sends)
  members: number;
  sends: number;
  engagements: number;
  uniqueMembers: number;
  lonLat: [number, number];
}

const COLOR_BY_RANK: string[] = [
  '#1e40af', // top
  '#2563eb',
  '#3b82f6',
  '#60a5fa',
  '#93c5fd',
];

function colorFor(value: number, maxValue: number): string {
  if (value <= 0 || maxValue <= 0) return '#cbd5e1';
  // Log scale so CA doesn't dwarf everything else
  const r = Math.log(value + 1) / Math.log(maxValue + 1);
  if (r >= 0.85) return COLOR_BY_RANK[0];
  if (r >= 0.65) return COLOR_BY_RANK[1];
  if (r >= 0.45) return COLOR_BY_RANK[2];
  if (r >= 0.25) return COLOR_BY_RANK[3];
  return COLOR_BY_RANK[4];
}

function radiusFor(value: number, maxValue: number): number {
  if (value <= 0 || maxValue <= 0) return 6;
  // sqrt scale so circle AREA is roughly proportional to value
  const r = Math.sqrt(value / maxValue);
  return Math.max(8, Math.min(50, r * 50 + 6));
}

export const GeoChoroplethMap: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const [view, setView] = useState<'members' | 'sends'>('members');
  const [mapReady, setMapReady] = useState(false);
  const [selected, setSelected] = useState<MarkerData | null>(null);

  const sendsQ = useGeoDistribution();
  const membersQ = useMemberGeoDistribution();
  // County data is only fetched (and only meaningfully shown) when a state
  // is selected. Today we only have CA county centroids — other states will
  // render their state markers but no county drill-down.
  const drilledState = selected?.state ?? null;
  const countyQ = useMemberCountyDistribution(drilledState);
  const isLoading = view === 'members' ? membersQ.isLoading : sendsQ.isLoading;

  // Build per-state marker data from both data sources merged.
  const markers = useMemo<MarkerData[]>(() => {
    const sendsByState = new Map<string, { sends: number; engagements: number; uniqueMembers: number }>();
    for (const c of sendsQ.data?.cells ?? []) {
      if (!c.state || c.state === 'Unknown') continue;
      const cur = sendsByState.get(c.state) ?? { sends: 0, engagements: 0, uniqueMembers: 0 };
      cur.sends += c.sends;
      cur.engagements += c.engagements;
      cur.uniqueMembers += c.unique_members;
      sendsByState.set(c.state, cur);
    }

    const membersByState = new Map<string, number>();
    for (const r of membersQ.data?.states ?? []) {
      if (!r.state || r.state === 'Unknown') continue;
      membersByState.set(r.state, (membersByState.get(r.state) ?? 0) + r.members);
    }

    const allStates = new Set<string>([...sendsByState.keys(), ...membersByState.keys()]);
    const out: MarkerData[] = [];
    for (const state of allStates) {
      const lonLat = US_STATE_CENTROIDS[state];
      if (!lonLat) continue; // unknown state code
      const sends = sendsByState.get(state) ?? { sends: 0, engagements: 0, uniqueMembers: 0 };
      const members = membersByState.get(state) ?? 0;
      out.push({
        state,
        value: view === 'members' ? members : sends.sends,
        members,
        sends: sends.sends,
        engagements: sends.engagements,
        uniqueMembers: sends.uniqueMembers,
        lonLat,
      });
    }
    return out.sort((a, b) => b.value - a.value);
  }, [sendsQ.data, membersQ.data, view]);

  const maxValue = markers[0]?.value ?? 1;
  const totalValue = markers.reduce((a, m) => a + m.value, 0);

  // Initialize map once.
  useEffect(() => {
    if (!MAPBOX_TOKEN || !containerRef.current || mapRef.current) return;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-98.5, 39.5],
      zoom: 3.4,
      interactive: true,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('load', () => setMapReady(true));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, []);

  // Render markers whenever data or active metric changes.
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    // Clear previous markers
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];

    for (const d of markers) {
      if (d.value <= 0) continue;
      const el = document.createElement('div');
      const radius = radiusFor(d.value, maxValue);
      const color = colorFor(d.value, maxValue);
      el.style.width = `${radius * 2}px`;
      el.style.height = `${radius * 2}px`;
      el.style.borderRadius = '50%';
      el.style.background = color;
      el.style.opacity = '0.75';
      el.style.border = '2px solid white';
      el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
      el.style.cursor = 'pointer';
      el.style.display = 'flex';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.color = 'white';
      el.style.fontSize = '10px';
      el.style.fontWeight = '600';
      el.style.fontFamily = 'system-ui, sans-serif';
      // Show state code inside larger circles
      if (radius > 16) el.textContent = d.state;

      const marker = new mapboxgl.Marker(el).setLngLat(d.lonLat).addTo(mapRef.current!);

      el.addEventListener('mouseenter', () => {
        if (!mapRef.current) return;
        if (popupRef.current) popupRef.current.remove();
        const html = `
          <div style="font-family:system-ui,sans-serif;font-size:12px;min-width:160px">
            <div style="font-weight:600;font-size:14px;margin-bottom:4px">${d.state}</div>
            <div>Members: <strong>${d.members.toLocaleString()}</strong></div>
            <div>Sends (since 2021): <strong>${d.sends.toLocaleString()}</strong></div>
            <div>Engaged (reply+click): <strong>${d.engagements.toLocaleString()}</strong></div>
          </div>
        `;
        popupRef.current = new mapboxgl.Popup({ closeButton: false, offset: radius + 4 })
          .setLngLat(d.lonLat)
          .setHTML(html)
          .addTo(mapRef.current!);
      });
      el.addEventListener('mouseleave', () => {
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }
      });
      el.addEventListener('click', () => {
        setSelected(d);
        mapRef.current!.flyTo({ center: d.lonLat, zoom: 6, duration: 1200 });
      });

      markersRef.current.push(marker);
    }
  }, [mapReady, markers, maxValue]);

  // County-level drill-down markers — drop on the map when the user clicks
  // a state for which we have county centroids (currently CA only).
  const countyMarkersRef = useRef<mapboxgl.Marker[]>([]);
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    // Clear any prior county markers
    for (const m of countyMarkersRef.current) m.remove();
    countyMarkersRef.current = [];

    if (!drilledState) return;

    // Pick the centroid lookup for the active state. Today only CA.
    const lookup = drilledState === 'CA' ? CA_COUNTY_CENTROIDS : null;
    if (!lookup) return;

    const counties = countyQ.data?.counties ?? [];
    if (!counties.length) return;

    const maxCountyMembers = Math.max(...counties.map((c) => c.members));

    for (const c of counties) {
      const lonLat = lookup[c.county];
      if (!lonLat) continue; // unknown county name (e.g., typo); skip silently
      const el = document.createElement('div');
      const radius = Math.max(6, Math.min(28, Math.sqrt(c.members / maxCountyMembers) * 28 + 4));
      const r = Math.log(c.members + 1) / Math.log(maxCountyMembers + 1);
      const color = r >= 0.85 ? '#7c2d12' : r >= 0.6 ? '#b45309' : r >= 0.35 ? '#d97706' : r >= 0.15 ? '#f59e0b' : '#fcd34d';
      el.style.width = `${radius * 2}px`;
      el.style.height = `${radius * 2}px`;
      el.style.borderRadius = '50%';
      el.style.background = color;
      el.style.opacity = '0.8';
      el.style.border = '1.5px solid white';
      el.style.boxShadow = '0 1px 2px rgba(0,0,0,0.25)';
      el.style.cursor = 'pointer';
      el.title = `${c.county} County — ${c.members.toLocaleString()} members`;

      const marker = new mapboxgl.Marker(el).setLngLat(lonLat).addTo(mapRef.current!);
      el.addEventListener('mouseenter', () => {
        if (!mapRef.current) return;
        if (popupRef.current) popupRef.current.remove();
        popupRef.current = new mapboxgl.Popup({ closeButton: false, offset: radius + 4 })
          .setLngLat(lonLat)
          .setHTML(
            `<div style="font-family:system-ui,sans-serif;font-size:12px">
              <div style="font-weight:600">${c.county} County</div>
              <div>Members: <strong>${c.members.toLocaleString()}</strong></div>
              <div>ZIP codes: <strong>${c.zip_codes.toLocaleString()}</strong></div>
              <div>Reachable: <strong>${c.reachable_members.toLocaleString()}</strong></div>
            </div>`,
          )
          .addTo(mapRef.current!);
      });
      el.addEventListener('mouseleave', () => {
        if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
      });
      countyMarkersRef.current.push(marker);
    }
  }, [mapReady, drilledState, countyQ.data]);

  // Token gate
  if (!MAPBOX_TOKEN) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div className="font-medium">Map unavailable</div>
        <div className="text-amber-800/90">
          Set <code className="text-xs">VITE_MAPBOX_API_KEY</code> on the frontend to render the US map.
          The table view still shows the data.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500 flex-1 min-w-0">
          {view === 'members' ? (
            <>Circle area scales with <strong>member count</strong> per state (full population from 2021+). Click any circle to zoom.</>
          ) : (
            <>Circle area scales with <strong>messages sent</strong> per state (legacy outreach since 2021). Click any circle to zoom.</>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selected && (
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                mapRef.current?.flyTo({ center: [-98.5, 39.5], zoom: 3.4, duration: 1000 });
              }}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50"
            >
              ← Reset view
            </button>
          )}
          <select
            value={view}
            onChange={(e) => setView(e.target.value as 'members' | 'sends')}
            className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
          >
            <option value="members">Members</option>
            <option value="sends">Sends (since 2021)</option>
          </select>
        </div>
      </div>

      <div className="flex gap-4">
        <div ref={containerRef} className="rounded-md border border-gray-200" style={{ height: '28rem', flex: 2, minHeight: '28rem' }} />
        <div className="flex-1 max-h-[28rem] overflow-y-auto">
          {isLoading ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : !markers.length ? (
            <div className="text-sm text-gray-500">No state data above suppression threshold.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-200 text-left uppercase tracking-wide text-gray-500">
                  <th className="py-1 pr-2">State</th>
                  <th className="py-1 pr-2 text-right">
                    {view === 'members' ? 'Members' : 'Sends'}
                  </th>
                  <th className="py-1 pr-2 text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                {markers.map((m) => {
                  const share = totalValue > 0 ? (m.value / totalValue) * 100 : 0;
                  const active = selected?.state === m.state;
                  return (
                    <tr
                      key={m.state}
                      onClick={() => {
                        setSelected(m);
                        mapRef.current?.flyTo({ center: m.lonLat, zoom: 6, duration: 1200 });
                      }}
                      className={`cursor-pointer border-b border-gray-100 last:border-0 ${active ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    >
                      <td className="py-1 pr-2 font-medium">
                        <span
                          className="inline-block h-3 w-3 rounded-full mr-1.5 align-middle"
                          style={{ backgroundColor: colorFor(m.value, maxValue) }}
                        />
                        {m.state}
                      </td>
                      <td className="py-1 pr-2 text-right tabular-nums">{m.value.toLocaleString()}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{share.toFixed(1)}%</td>
                    </tr>
                  );
                })}
                <tr className="border-t border-gray-300 font-medium">
                  <td className="py-1 pr-2 text-gray-700">{markers.length} states</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{totalValue.toLocaleString()}</td>
                  <td className="py-1 pr-2" />
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selected && (
        <div className="space-y-2">
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
            <span className="font-medium">{selected.state}:</span>{' '}
            {selected.members.toLocaleString()} members ·{' '}
            {selected.sends.toLocaleString()} sends since 2021 ·{' '}
            {selected.engagements.toLocaleString()} engaged
            {selected.uniqueMembers > 0 && (
              <> · {selected.uniqueMembers.toLocaleString()} unique recipients reached</>
            )}
          </div>
          {drilledState && (
            <div className="text-xs text-gray-500">
              {drilledState === 'CA' ? (
                <>Orange/amber circles show <strong>per-county member counts</strong> — hover for ZIP + reachable counts. {countyQ.data?.counties?.length ?? 0} counties with data.</>
              ) : (
                <>County-level drill-down not yet available for {drilledState} (centroids only seeded for CA). Member counts shown in the table below.</>
              )}
            </div>
          )}
          {drilledState && countyQ.data?.counties && countyQ.data.counties.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-700 hover:text-gray-900">
                Top counties in {drilledState} ({countyQ.data.counties.length})
              </summary>
              <table className="mt-2 w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500 uppercase tracking-wide">
                    <th className="py-1 pr-2">County</th>
                    <th className="py-1 pr-2 text-right">Members</th>
                    <th className="py-1 pr-2 text-right">ZIPs</th>
                    <th className="py-1 pr-2 text-right">Reachable</th>
                  </tr>
                </thead>
                <tbody>
                  {countyQ.data.counties.slice(0, 20).map((c) => (
                    <tr key={`${c.state}|${c.county}`} className="border-b border-gray-100 last:border-0">
                      <td className="py-1 pr-2 font-medium text-gray-900">{c.county}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{c.members.toLocaleString()}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{c.zip_codes.toLocaleString()}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{c.reachable_members.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
};
